import { getItem, setItem, removeItem, getAllKeys } from '../../../../../utils/localStorageHelper';
import { ShipmentLineRow, ShipmentLineDto } from '../../../../../types/shipmentLine';
import { pickingService } from '../../../../../services/pickingService';

/**
 * TW 36430608: kollidialogen husker det operatøren har tastet til plukklista er avsluttet.
 *
 * Kladden ligger i nettleserens localStorage (bevisst ikke på serveren — ingenting skrives
 * før Bekreft, som før). Nøkkelen inneholder tenant, selskap og plukkliste, så en kladd
 * havner aldri på en annen liste eller i et annet selskap (PListID kan finnes i flere
 * selskap i samme database). Kladden har ikke utløp på tid: den slettes når lista avsluttes,
 * når lista åpnes med status ≥ 50, og ved oppryddingen når plukkmodulen åpnes.
 */

const PREFIX = 'deliveryInfo';

export interface DraftDeliveryFields {
  totalVolume: string;
  totalWeight: string;
  numOfPackages: string;
  numOfPallets: string;
  carrierName: string;
  deliveryMethodNo: number | null;
  packZoneID: string;
  packageRemark: string;
}

export interface DeliveryInfoDraft {
  version: 1;
  savedAt: string;
  deliveryMode: string;
  pListData: DraftDeliveryFields;
  orders: Array<DraftDeliveryFields & { orderNo: number; shipmentLines: ShipmentLineRow[] }>;
  consolidationLines: ShipmentLineRow[];
}

export const deliveryDraftKey = (customerNo: string, companyNo: string, pListId: number): string =>
  `${PREFIX}:${customerNo}:${companyNo}:${pListId}`;

export const loadDeliveryDraft = (key: string): DeliveryInfoDraft | null => {
  const draft = getItem<DeliveryInfoDraft>(key);
  return draft && draft.version === 1 ? draft : null;
};

export const saveDeliveryDraft = (key: string, draft: Omit<DeliveryInfoDraft, 'version' | 'savedAt'>): void => {
  setItem<DeliveryInfoDraft>(key, { ...draft, version: 1, savedAt: new Date().toISOString() });
};

export const clearDeliveryDraft = (key: string): void => {
  removeItem(key);
};

/** Kladdene for innlogget tenant og selskap, med plukkliste-ID. */
export const listDeliveryDrafts = (customerNo: string, companyNo: string): Array<{ key: string; pListId: number }> => {
  const prefix = `${PREFIX}:${customerNo}:${companyNo}:`;
  return getAllKeys()
    .filter(k => k.startsWith(prefix))
    .map(k => ({ key: k, pListId: parseInt(k.slice(prefix.length), 10) }))
    .filter(d => !isNaN(d.pListId));
};

/**
 * Opprydding når plukkmodulen åpnes: kladder for lister som er avsluttet (status ≥ 50,
 * f.eks. på en annen enhet) eller slettet, fjernes. Feil ved oppslag lar kladden stå —
 * heller en kladd for mye enn å miste det operatøren har tastet.
 */
export const sweepDeliveryDrafts = async (customerNo: string, companyNo: string): Promise<void> => {
  for (const { key, pListId } of listDeliveryDrafts(customerNo, companyNo)) {
    try {
      const status = await pickingService.getPickingListStatus(pListId);
      if (status == null || status >= 50) {
        removeItem(key);
      }
    } catch {
      // nettverksfeil o.l. — prøv igjen neste gang modulen åpnes
    }
  }
};

const toRow = (line: ShipmentLineDto): ShipmentLineRow => ({
  lineID: line.lineID,
  transitPackagingID: line.transitPackagingID,
  transitPackagingDescr: line.transitPackagingDescr,
  description1: line.description1,
  numOfPackages: line.numOfPackages,
  volume: line.volume,
  weight: line.weight,
  height: line.height,
  width: line.width,
  length: line.length,
  pListID: line.pListID,
  orderNo: line.orderNo,
  sorting: line.sorting,
  isNew: false
});

/**
 * Etter at kollilinjene er lagret på serveren: skriv de lagrede linjene (med lineID) inn i
 * kladden, så en ny Bekreft oppdaterer dem i stedet for å opprette dem på nytt.
 * Ordrer som ikke er med i savedByOrder, røres ikke.
 */
export const applySavedLinesToDraft = (key: string, savedByOrder: Map<number, ShipmentLineDto[]>): void => {
  const draft = loadDeliveryDraft(key);
  if (!draft) return;

  const orders = draft.orders.map(o =>
    savedByOrder.has(o.orderNo)
      ? { ...o, shipmentLines: (savedByOrder.get(o.orderNo) ?? []).map(toRow) }
      : o
  );
  const consolidationLines = draft.deliveryMode === 'consolidate'
    ? Array.from(savedByOrder.values()).flat().map(toRow)
    : draft.consolidationLines;

  saveDeliveryDraft(key, {
    deliveryMode: draft.deliveryMode,
    pListData: draft.pListData,
    orders,
    consolidationLines
  });
};
